import * as Sentry from "@sentry/react";
import { isEqual } from "lodash";
import { Check, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import PaginatedLoader from "./component/PaginatedLoader";
import usePaginatedQuery from "./hooks/UseSelectPagination";
import type { BasePaginatedSelectProps } from "./interface/paginatedSelect.interface";

import type { LabelValue } from "@/app/app.interface";
import { CustomToolTip } from "@/components/reusables/CustomToolTip";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTriggerButton,
} from "@/components/ui/popover";
import useDebounce from "@/hooks/state/useDebounce";
import { cn } from "@/utils/core/generalUtils";
import { customToast } from "@/utils/custom-toast";

interface AsyncMultiSelectProps<T, P = ""> extends Omit<
  BasePaginatedSelectProps<T, P>,
  "value" | "onChange"
> {
  defaultLabels?: LabelValue[];
  values?: string[];
  onChange: (
    values: string[],
    objects?: T[],
    selectedLabels?: string[],
  ) => void;
  clearable?: boolean;
  maxSelectedDisplay?: number;
  allowSelectAll?: boolean;
  isFilter?: boolean;
  mandatoryValues?: string[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  portal?: boolean;
}

export const AsyncMultiSelect = <T, P = "">({
  useQuery,
  baseParams,
  filterConfig,
  preload,
  filterFn,
  renderConfig,
  defaultLabel,
  defaultLabels,
  notFound,
  loadingSkeleton,
  label,
  placeholder = "Select...",
  values = [],
  onChange,
  disabled = false,
  className,
  triggerClassName,
  noResultsMessage,
  clearable = true,
  limit = 10,
  loadMoreThreshold = 100,
  side,
  maxSelectedDisplay = 3,
  allowSelectAll = false,
  filterQueryList,
  isFilter = false,
  mandatoryValues = [],
  open: controlledOpen,
  onOpenChange,
  portal = false,
  messages = {
    mandatoryChoiceDeselectError: "Required choice can't be removed.",
  },
}: AsyncMultiSelectProps<T, P>): JSX.Element => {
  const [mounted, setMounted] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const [allOptions, setAllOptions] = useState<T[]>([]);
  const [displayOptions, setDisplayOptions] = useState<T[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>(values);
  const [selectedOptions, setSelectedOptions] = useState<T[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [selectedOptionsCache, setSelectedOptionsCache] = useState<
    Map<string, T>
  >(new Map());

  const prevOptionsRef = useRef<T[]>([]);
  const prevOffsetRef = useRef<number>(0);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, preload ? 0 : 500);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const skipQuery = !open && !preload;

  const {
    data,
    error,
    isLoading,
    isFetching,
    paginationState,
    setLimit,
    setQuery,
    loadMore,
    hasMore,
  } = usePaginatedQuery(
    useQuery,
    baseParams,
    {
      ...filterConfig,
      limitKey: filterConfig?.limitKey || "limit",
    },
    skipQuery,
  );

  const getOptionValue = useMemo(() => {
    return (option: T): string => {
      const optionValue = option[renderConfig.valueKey];

      return String(optionValue || "");
    };
  }, [renderConfig.valueKey]);

  const mandatoryValueSet = useMemo(() => {
    return new Set(mandatoryValues);
  }, [mandatoryValues]);

  const renderOption = useCallback(
    (option: T): React.ReactNode => {
      const getBaseLabel = (): React.ReactNode => {
        if (renderConfig.labelRenderer) {
          return renderConfig.labelRenderer(option);
        }

        if (renderConfig.labelKey) {
          const label = option[renderConfig.labelKey];

          return (
            <div className='line-clamp-4' title={String(label || "")}>
              {String(label || "")}
            </div>
          );
        }

        const values = option[renderConfig.valueKey];

        return (
          <div className='line-clamp-4' title={String(values || "")}>
            {String(values || "")}
          </div>
        );
      };

      const baseLabel = getBaseLabel();

      if (renderConfig.showCount && renderConfig.countKey) {
        const count = Number(option[renderConfig.countKey] || 0);

        return (
          <div className='flex items-center justify-between w-full'>
            <span>{baseLabel}</span>
            {count > 0 && (
              <div className='bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px] min-w-[20px] text-center'>
                {count}
              </div>
            )}
          </div>
        );
      }

      return baseLabel;
    },
    [renderConfig],
  );

  const getIsMandatory = useCallback(
    (option: T): boolean => {
      if (!renderConfig.isMandatory) {
        return false;
      }
      try {
        return renderConfig.isMandatory(option);
      } catch (error) {
        Sentry.captureException(error);

        return false;
      }
    },
    [renderConfig],
  );

  const getIsDisabled = useCallback(
    (option: T): boolean => {
      if (!renderConfig.isDisabled) {
        return false;
      }
      try {
        return renderConfig.isDisabled(option);
      } catch (error) {
        Sentry.captureException(error);

        return false;
      }
    },
    [renderConfig],
  );

  const getOptionLabel = useCallback(
    (option: T): string => {
      if (renderConfig.labelRenderer) {
        const rendered = renderConfig.labelRenderer(option);
        if (typeof rendered === "string") {
          return rendered;
        }
      }

      if (renderConfig.labelKey) {
        return String(option[renderConfig.labelKey] ?? "");
      }

      return String(option[renderConfig.valueKey] ?? "");
    },
    [renderConfig],
  );

  const getDisplayValue = useCallback(
    (option: T): React.ReactNode => {
      if (renderConfig.customRender) {
        return renderConfig.customRender(option);
      }

      if (renderConfig.labelRenderer) {
        return renderConfig.labelRenderer(option);
      }

      const displayKey = renderConfig.labelKey || renderConfig.valueKey;

      return String(option[displayKey] ?? "");
    },
    [renderConfig],
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) {
      return;
    }

    const valuesChanged =
      values.length !== selectedValues.length ||
      values.some((val, index) => val !== selectedValues[index]);

    if (valuesChanged) {
      setSelectedValues(values);
    }
  }, [values, mounted]);

  useEffect(() => {
    if (paginationState.limit !== limit) {
      setLimit(limit);
    }
  }, [limit, paginationState.limit, setLimit]);

  const setIsLoadingMoreCallback = useCallback((loading: boolean) => {
    setIsLoadingMore(loading);
  }, []);

  useEffect(() => {
    if (mounted && !skipQuery) {
      setQuery(debouncedSearchTerm || undefined);
      setIsLoadingMoreCallback(false);
    }
  }, [
    debouncedSearchTerm,
    setQuery,
    mounted,
    skipQuery,
    setIsLoadingMoreCallback,
  ]);

  useEffect(() => {
    const options = data?.data || [];
    if (!skipQuery && options.length > 0) {
      const optionsChanged =
        options.length !== prevOptionsRef.current.length ||
        options.some(
          (opt, index) =>
            getOptionValue(opt) !==
              getOptionValue(prevOptionsRef.current[index] || ({} as T)) ||
            !isEqual(opt, prevOptionsRef.current[index] || {}),
        );

      const offsetChanged = paginationState.offset !== prevOffsetRef.current;

      if (optionsChanged || offsetChanged) {
        setSelectedOptionsCache((prevCache) => {
          const newCache = new Map(prevCache);
          let cacheUpdated = false;

          options.forEach((option) => {
            const optionValue = getOptionValue(option);
            if (optionValue) {
              const existingOption = prevCache.get(optionValue);
              const isDifferent =
                !existingOption || !isEqual(existingOption, option);

              if (
                isDifferent &&
                (selectedValues.includes(optionValue) ||
                  prevCache.has(optionValue))
              ) {
                newCache.set(optionValue, option);
                cacheUpdated = true;
              }
            }
          });

          return cacheUpdated ? newCache : prevCache;
        });

        if (paginationState.offset === 0) {
          setAllOptions(options);
        } else {
          setAllOptions((prev) => {
            const newOptions = options.filter(
              (newOpt) =>
                !prev.some(
                  (existingOpt) =>
                    getOptionValue(existingOpt) === getOptionValue(newOpt),
                ),
            );

            return [...prev, ...newOptions];
          });
        }

        prevOptionsRef.current = options;
        prevOffsetRef.current = paginationState.offset;
      }

      setIsLoadingMore(false);
    } else if (!skipQuery && paginationState.offset === 0) {
      setAllOptions([]);
      prevOptionsRef.current = [];
      prevOffsetRef.current = 0;
    }
  }, [
    data?.data,
    paginationState.offset,
    getOptionValue,
    skipQuery,
    values,
    selectedValues,
  ]);

  useEffect(() => {
    let filteredOptions = allOptions;

    if (preload && debouncedSearchTerm && filterFn) {
      filteredOptions = allOptions.filter((option) =>
        filterFn(option, debouncedSearchTerm),
      );
    }
    if (filterQueryList) {
      filteredOptions = filterQueryList(filteredOptions);
    }
    // TODO: Remove this only if after testing that it's not needed
    // const optionValues = new Set(filteredOptions.map(getOptionValue));
    // const additionalOptions: T[] = [];

    // selectedOptionsCache.forEach((cachedOption, cachedValue) => {
    //   if (!optionValues.has(cachedValue)) {
    //     if (preload && debouncedSearchTerm && filterFn) {
    //       if (filterFn(cachedOption, debouncedSearchTerm)) {
    //         additionalOptions.push(cachedOption);
    //       }
    //     } else if (!debouncedSearchTerm || preload) {
    //       additionalOptions.push(cachedOption);
    //     }
    //   }
    // });

    setDisplayOptions([...filteredOptions]);
  }, [
    preload,
    allOptions,
    debouncedSearchTerm,
    filterFn,
    selectedOptionsCache,
    getOptionValue,
    filterQueryList,
  ]);

  useEffect(() => {
    if (selectedValues.length > 0) {
      const options: T[] = [];

      selectedValues.forEach((val) => {
        let option = displayOptions.find((opt) => getOptionValue(opt) === val);

        if (!option && selectedOptionsCache.has(val)) {
          option = selectedOptionsCache.get(val);
        }

        if (option) {
          options.push(option);
        }
      });

      setSelectedOptions(options);
    } else {
      setSelectedOptions([]);
    }
  }, [selectedValues, displayOptions, getOptionValue, selectedOptionsCache]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (preload || !hasMore || isFetching || isLoading || isLoadingMore) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom =
        scrollHeight - scrollTop - clientHeight < loadMoreThreshold;

      if (isNearBottom) {
        setIsLoadingMore(true);
        loadMore();
      }
    },
    [
      preload,
      hasMore,
      isFetching,
      isLoading,
      isLoadingMore,
      loadMoreThreshold,
      loadMore,
    ],
  );

  const getOptionLabelForValue = useCallback(
    (val: string): string => {
      const option =
        selectedOptions.find((opt) => getOptionValue(opt) === val) ||
        selectedOptionsCache.get(val);

      if (option) {
        return getOptionLabel(option);
      }

      if (defaultLabels) {
        const matchingDefault = defaultLabels.find((opt) => opt.value === val);

        if (matchingDefault) {
          return String(matchingDefault.label);
        }
      }

      return defaultLabel || val;
    },
    [
      selectedOptions,
      getOptionValue,
      getOptionLabel,
      selectedOptionsCache,
      defaultLabels,
      defaultLabel,
    ],
  );

  const getSelectedLabelsForValues = useCallback(
    (valuesList: string[]): string[] => {
      return valuesList.map((value) => getOptionLabelForValue(value));
    },
    [getOptionLabelForValue],
  );

  const handleSelect = useCallback(
    (currentValue: string, _label: string) => {
      // Update cache with newly selected options
      const option = displayOptions.find(
        (opt) => getOptionValue(opt) === currentValue,
      );
      const isSelected = selectedValues.includes(currentValue);
      const isMandatory = option ? getIsMandatory(option) : false;
      const isDisabled = option ? getIsDisabled(option) : false;

      if (isDisabled) {
        return;
      }

      if (isSelected && isMandatory) {
        customToast.info(messages.mandatoryChoiceDeselectError!);

        return;
      }

      let newValues: string[];
      if (isSelected) {
        newValues = selectedValues.filter((val) => val !== currentValue);
      } else {
        newValues = [...selectedValues, currentValue];
      }

      setSelectedValues(newValues);

      if (option && !isSelected) {
        const newCache = new Map(selectedOptionsCache);
        newCache.set(currentValue, option);
        setSelectedOptionsCache(newCache);
      }

      if (!isFilter) {
        // Get all selected option objects
        const selectedObjects = newValues
          .map((val) => {
            return (
              displayOptions.find((opt) => getOptionValue(opt) === val) ||
              selectedOptionsCache.get(val)
            );
          })
          .filter(Boolean) as T[];

        onChange(
          newValues,
          selectedObjects,
          getSelectedLabelsForValues(newValues),
        );
      }
    },
    [
      selectedValues,
      onChange,
      displayOptions,
      getOptionValue,
      selectedOptionsCache,
      isFilter,
      getIsMandatory,
      getSelectedLabelsForValues,
    ],
  );

  const getDisplayValueForSelected = useCallback(
    (val: string): React.ReactNode => {
      // First try to find in selected options (fetched data)
      const option = selectedOptions.find((opt) => getOptionValue(opt) === val);
      if (option) {
        return getDisplayValue(option);
      }

      // Then try cached options
      const cachedOption = selectedOptionsCache.get(val);
      if (cachedOption) {
        return getDisplayValue(cachedOption);
      }

      // Use defaultLabels if provided
      if (defaultLabels) {
        const matchingDefault = defaultLabels.find((opt) => opt.value === val);

        if (
          matchingDefault?.isInactive &&
          renderConfig.defaultDeactivateLabelRender
        ) {
          return renderConfig.defaultDeactivateLabelRender(
            matchingDefault.label,
          );
        }

        if (matchingDefault) {
          return String(matchingDefault.label);
        }
      }

      // Fall back to defaultLabel or the values itself
      return defaultLabel || val;
    },
    [
      selectedOptions,
      getOptionValue,
      getDisplayValue,
      selectedOptionsCache,
      defaultLabels,
      defaultLabel,
      renderConfig,
    ],
  );

  const isValueMandatory = useCallback(
    (selectedValue: string): boolean => {
      if (mandatoryValueSet.has(selectedValue)) {
        return true;
      }

      const option =
        selectedOptions.find((opt) => getOptionValue(opt) === selectedValue) ||
        selectedOptionsCache.get(selectedValue);

      return option ? getIsMandatory(option) : false;
    },
    [
      mandatoryValueSet,
      selectedOptions,
      selectedOptionsCache,
      getOptionValue,
      getIsMandatory,
    ],
  );

  const defaultLabelsMap = useMemo(() => {
    if (!defaultLabels) {
      return new Map();
    }
    return new Map(defaultLabels.map((label) => [label.value, label]));
  }, [defaultLabels]);

  const isSelectedValueInactive = useCallback(
    (selectedValue: string): boolean => {
      const matchingDefault = defaultLabelsMap.get(selectedValue);
      return !!matchingDefault?.isInactive;
    },
    [defaultLabelsMap],
  );

  const handleSelectAll = useCallback(() => {
    const selectedMandatoryValues = selectedValues.filter(isValueMandatory);
    const optionMandatoryValues = displayOptions
      .filter((opt) => getIsMandatory(opt))
      .map(getOptionValue);
    const nextMandatoryValues = [
      ...new Set([...selectedMandatoryValues, ...optionMandatoryValues]),
    ];

    const selectableValues = displayOptions
      .filter((opt) => !getIsDisabled(opt))
      .map(getOptionValue);

    const hasAllSelectable = selectableValues.every((val) =>
      selectedValues.includes(val),
    );

    if (hasAllSelectable) {
      setSelectedValues(nextMandatoryValues);

      if (!isFilter) {
        onChange(
          nextMandatoryValues,
          displayOptions.filter((opt) => getIsMandatory(opt)),
          getSelectedLabelsForValues(nextMandatoryValues),
        );
      }
    } else {
      const allValues = [
        ...new Set([...nextMandatoryValues, ...selectableValues]),
      ];
      setSelectedValues(allValues);

      const newCache = new Map(selectedOptionsCache);
      displayOptions.forEach((option) => {
        if (!getIsDisabled(option)) {
          const value = getOptionValue(option);
          newCache.set(value, option);
        }
      });
      setSelectedOptionsCache(newCache);

      if (!isFilter) {
        onChange(
          allValues,
          displayOptions.filter(
            (opt) => !getIsDisabled(opt) || getIsMandatory(opt),
          ),
          getSelectedLabelsForValues(allValues),
        );
      }
    }
  }, [
    displayOptions,
    getOptionValue,
    getIsMandatory,
    getIsDisabled,
    selectedValues,
    isFilter,
    onChange,
    selectedOptionsCache,
    getSelectedLabelsForValues,
    isValueMandatory,
  ]);

  const handleRemoveSelected = useCallback(
    (valueToRemove: string) => {
      if (isValueMandatory(valueToRemove)) {
        customToast.info(messages.mandatoryChoiceDeselectError!);

        return;
      }

      const newValues = selectedValues.filter((val) => val !== valueToRemove);
      setSelectedValues(newValues);

      if (!isFilter) {
        const selectedObjects = newValues
          .map((val) => {
            return (
              displayOptions.find((opt) => getOptionValue(opt) === val) ||
              selectedOptionsCache.get(val)
            );
          })
          .filter(Boolean) as T[];

        onChange(
          newValues,
          selectedObjects,
          getSelectedLabelsForValues(newValues),
        );
      } else if (newValues.length === 0) {
        // In filter mode, if the last item is removed, commit clear so placeholder shows
        onChange([], []);
      }
    },
    [
      selectedValues,
      onChange,
      displayOptions,
      getOptionValue,
      selectedOptionsCache,
      isFilter,
      isValueMandatory,
      getSelectedLabelsForValues,
      messages.mandatoryChoiceDeselectError,
    ],
  );

  const handleClearAll = useCallback(() => {
    const nextMandatoryValues = selectedValues.filter(isValueMandatory);

    setSelectedValues(nextMandatoryValues);

    if (!isFilter) {
      const selectedObjects = nextMandatoryValues
        .map(
          (val) =>
            displayOptions.find((opt) => getOptionValue(opt) === val) ||
            selectedOptionsCache.get(val),
        )
        .filter(Boolean) as T[];

      onChange(
        nextMandatoryValues,
        selectedObjects,
        getSelectedLabelsForValues(nextMandatoryValues),
      );
    } else {
      onChange(nextMandatoryValues, []);
    }

    setInternalOpen(false);
    onOpenChange?.(false);
    setSearchTerm("");
  }, [
    selectedValues,
    displayOptions,
    getOptionValue,
    selectedOptionsCache,
    isFilter,
    onChange,
    getSelectedLabelsForValues,
    isValueMandatory,
    onOpenChange,
  ]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setInternalOpen(newOpen);
      onOpenChange?.(newOpen);
      if (!newOpen) {
        setSearchTerm("");
        if (isFilter) {
          // Revert draft selections when closing without applying
          setSelectedValues(values);
        }
      }
    },
    [isFilter, values, onOpenChange],
  );

  // Handle keyboard events for Tab and Esc
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) {
        return;
      }

      if (e.key === "Tab" && !open) {
        // Open dropdown on Tab when focused and closed
        e.preventDefault();
        handleOpenChange(true);
      } else if (e.key === "Escape" && open) {
        // Close dropdown on Esc
        e.preventDefault();
        e.stopPropagation();
        handleOpenChange(false);
      }
    },
    [disabled, open, handleOpenChange],
  );

  const handleApply = useCallback(() => {
    onChange(
      selectedValues,
      selectedOptions,
      getSelectedLabelsForValues(selectedValues),
    );
    handleOpenChange(false);
  }, [
    onChange,
    selectedValues,
    selectedOptions,
    getSelectedLabelsForValues,
    handleOpenChange,
  ]);

  const hasRemovableSelectedValues = useMemo(() => {
    const valuesForRemoval = isFilter ? values : selectedValues;

    return valuesForRemoval.some((selectedValue) => {
      return !isValueMandatory(selectedValue);
    });
  }, [isFilter, values, selectedValues, isValueMandatory]);

  const triggerDisplayValue = useMemo(() => {
    const valuesForTrigger = isFilter ? values : selectedValues;

    if (valuesForTrigger.length === 0) {
      return placeholder;
    }

    if (valuesForTrigger.length <= maxSelectedDisplay) {
      return (
        <div className='flex flex-wrap gap-1.5'>
          {valuesForTrigger.map((val) => {
            const displayValue = getDisplayValueForSelected(val);

            return (
              <Badge
                key={val}
                variant='outline'
                className={cn(
                  "text-xs flex items-center gap-1 max-w-[180px] whitespace-nowrap",
                  isSelectedValueInactive(val) && "opacity-50",
                )}
              >
                <span className='truncate flex-1 min-w-0'>{displayValue}</span>
                {!disabled && !isValueMandatory(val) && (
                  <X
                    className='w-3 h-3 cursor-pointer hover:text-destructive flex-shrink-0'
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveSelected(val);
                    }}
                  />
                )}
              </Badge>
            );
          })}
        </div>
      );
    }

    return (
      <CustomToolTip
        toolTipContent={
          <div className='flex flex-col gap-1'>
            {valuesForTrigger.map((val) => {
              const displayValue = getDisplayValueForSelected(val);

              return (
                <span key={val} className='text-xs'>
                  {displayValue}
                </span>
              );
            })}
          </div>
        }
        toolTipTrigger={
          <div className='flex items-center gap-2'>
            <span className='text-sm'>
              {valuesForTrigger.length} {label.toLowerCase()} selected
            </span>
          </div>
        }
      />
    );
  }, [
    selectedValues,
    values,
    maxSelectedDisplay,
    placeholder,
    getDisplayValueForSelected,
    disabled,
    handleRemoveSelected,
    label,
    isFilter,
    isValueMandatory,
  ]);

  const loadingStates = useMemo(() => {
    const isFirstLoad = paginationState.offset === 0;
    const hasSearchQuery = searchTerm.trim().length > 0;

    return {
      showSearchLoader: hasSearchQuery && isFetching && !isLoadingMore,
      showMainSkeleton:
        (isLoading || (isFetching && isFirstLoad && !isLoadingMore)) &&
        !preload &&
        !searchTerm,
      showLoadMoreIndicator:
        isFetching && isLoadingMore && hasMore && displayOptions.length > 0,
    };
  }, [
    paginationState.offset,
    searchTerm,
    isFetching,
    isLoading,
    isLoadingMore,
    hasMore,
    displayOptions.length,
    preload,
  ]);

  const isValidDisplayValue = useMemo(() => {
    return placeholder !== triggerDisplayValue;
  }, [placeholder, triggerDisplayValue]);

  // Auto-focus the search input when popover opens and content is ready
  useEffect((): (() => void) | undefined => {
    if (!open || loadingStates.showMainSkeleton) {
      return undefined;
    }

    const timer = setTimeout((): void => {
      searchInputRef.current?.focus();
    }, 0);

    return (): void => clearTimeout(timer);
  }, [open, loadingStates.showMainSkeleton]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={true}>
      <PopoverTriggerButton
        ref={triggerRef}
        role='combobox'
        className={cn(
          "justify-between min-h-10 h-auto bg-inherit hover:bg-inherit active:scale-100 ",
          disabled && "opacity-50 cursor-not-allowed",
          triggerClassName,
        )}
        contentClassName={cn(
          "line-clamp-none block w-full overflow-visible break-normal",
        )}
        disabled={disabled}
        isArrowVisible={!isValidDisplayValue}
        side={side}
        onKeyDown={handleKeyDown}
        customIcon={
          clearable &&
          isValidDisplayValue &&
          hasRemovableSelectedValues &&
          !disabled && (
            <X
              className='w-4 h-4 ml-2 opacity-50'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClearAll();
              }}
            />
          )
        }
      >
        <div
          className={cn("block w-full", !isValidDisplayValue && "opacity-30")}
        >
          {triggerDisplayValue}
        </div>
      </PopoverTriggerButton>
      <PopoverContent
        className={cn(
          "p-2 popover-content-width-full border border-input rounded-[0.5rem] shadow-select min-w-fit overflow-hidden",
          className,
        )}
        align='start'
        portal={portal}
        side={side}
      >
        <Command shouldFilter={false} className='min-h-0'>
          {loadingStates.showMainSkeleton || loadingSkeleton ? (
            <PaginatedLoader />
          ) : (
            <>
              <div className='relative w-full'>
                <CommandInput
                  ref={searchInputRef}
                  placeholder={`Search ${label.toLowerCase()}...`}
                  value={searchTerm}
                  onValueChange={setSearchTerm}
                />
                {loadingStates.showSearchLoader && (
                  <div className='absolute flex items-center transform -translate-y-1/2 right-2 top-1/2'>
                    <Loader2 className='w-4 h-4 animate-spin text-muted-foreground' />
                  </div>
                )}
              </div>
              <CommandList
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className={cn(
                  "min-h-0 overflow-y-auto scrollbar-none",
                  isFilter
                    ? "max-h-[min(15rem,calc(var(--radix-popover-content-available-height)-7.5rem))]"
                    : "max-h-[min(15rem,calc(var(--radix-popover-content-available-height)-4rem))]",
                )}
              >
                {error && (
                  <div className='p-4 text-center text-destructive'>
                    Failed to fetch options
                  </div>
                )}

                {!isLoading &&
                  !error &&
                  displayOptions.length === 0 &&
                  !loadingStates.showMainSkeleton &&
                  (notFound || (
                    <CommandEmpty>
                      {noResultsMessage ?? `No ${label.toLowerCase()} found.`}
                    </CommandEmpty>
                  ))}

                {displayOptions.length > 0 && (
                  <CommandGroup>
                    {allowSelectAll && (
                      <CommandItem
                        onSelect={handleSelectAll}
                        className='mb-1 font-medium border-b'
                      >
                        <div className='flex items-center gap-2'>
                          <div className='flex items-center justify-center w-4 h-4 border rounded-sm'>
                            {selectedValues.length ===
                              displayOptions.length && (
                              <Check className='w-3 h-3' />
                            )}
                            {selectedValues.length > 0 &&
                              selectedValues.length < displayOptions.length && (
                                <div className='w-2 h-2 bg-current rounded-sm' />
                              )}
                          </div>
                          Select All ({displayOptions.length})
                        </div>
                      </CommandItem>
                    )}

                    {displayOptions.map((option) => {
                      const optionValue = getOptionValue(option);
                      const isSelected = selectedValues.includes(optionValue);
                      const label = getOptionLabel(option);
                      const isMandatory = getIsMandatory(option);
                      const isDisabled = getIsDisabled(option);

                      const getDisabledReason = (): string => {
                        if (isMandatory) {
                          return "This selection cannot be removed";
                        }
                        if (isDisabled) {
                          return "This option is disabled";
                        }

                        return "";
                      };

                      const title = getDisabledReason();

                      return (
                        <CommandItem
                          key={optionValue}
                          value={optionValue}
                          onSelect={(value) => handleSelect(value, label)}
                          disabled={isDisabled}
                          className={cn(
                            "p-2 content-stretch group text-sm",
                            isDisabled
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer",
                          )}
                        >
                          <div className='flex items-center w-full gap-2'>
                            <div
                              title={title}
                              className={cn(
                                "flex items-center justify-center w-4 h-4 border rounded-sm shrink-0 p-1",
                                isSelected && "bg-primary",
                                (isMandatory || isDisabled) && "opacity-50",
                              )}
                            >
                              {isSelected && (
                                <Check className='w-3 h-3 text-primary-foreground' />
                              )}
                            </div>
                            <div className='whitespace-normal break-words flex-1'>
                              {renderOption(option)}
                            </div>
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}

                {loadingStates.showLoadMoreIndicator && (
                  <div className='p-3 text-center border-t bg-muted/5'>
                    <div className='flex items-center justify-center gap-2'>
                      <Loader2 className='w-4 h-4 animate-spin text-muted-foreground' />
                      <p className='text-sm text-muted-foreground'>
                        Loading more...
                      </p>
                    </div>
                  </div>
                )}
              </CommandList>
            </>
          )}
        </Command>
        {isFilter && (
          <div className='flex items-center justify-end gap-2 pt-2 border-t'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-8'
              onClick={() => {
                setSelectedValues([]);
                onChange([], []);
              }}
            >
              Reset
            </Button>
            <Button
              type='button'
              size='sm'
              className='h-8'
              disabled={
                selectedValues.length === values.length &&
                selectedValues.every((v, i) => v === values[i])
              }
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
